import { useMemo } from 'react';

import { useSession } from '@eneco/auth';
import { useSelfServiceAccount } from '@eneco/auth-be-self-service';
import RichText from '@eneco/components/RichText/RichText';
import {
  getEnecoBeXapiSiteApiV1AccountsByAccountNumberDeliveryAddressesByAddressIdentifierAdvanceAmounts,
  postEnecoBeXapiSiteApiV1AccountsByAccountNumberDeliveryAddressesByAddressIdentifierAdvanceAmounts,
} from '@eneco/dc-be/client';
import useAuthenticatedDCBE from '@eneco/dc-be/hooks/useAuthenticatedDCBE';
import unwrapData from '@eneco/dc-be/utils/unwrapData';
import { useRouter } from '@eneco/next';
import { NotificationBoxProps } from '@eneco/sparky/types';
import { useTracking } from '@eneco/tracking';
import { useSWRConfig } from 'swr';

import { useSetNotificationAndScrollToTop } from '../../../../hooks/useSetNotificationAndScrollToTop';
import { ADDRESS_PARAM } from '../../../../utils/addSearchParams';
import { resolveDeliveryAddressFromQuery } from '../../../../utils/selectedDeliveryAddress';
import { calculateReconcileGlobalWithExistingCurrentAmounts } from '../utils/calculateReconcileAdvanceAmount';
import { canEditAdvanceAmount } from '../utils/canEditAdvances';
import { filteredAdvancesWithActiveContractAndCurrentAmount, hasActiveContract } from '../utils/filterActiveAdvances';

// https://dev.azure.com/enecomanagedcloud/Eneco%20Belgium/_workitems/edit/656502
export const isPaymentInMeterReadingMonth = (payment: {
  details?: { monthsUntilMeterReadingMonth?: number | null };
  meterDetails?: { meterType?: string };
}) => {
  const monthsUntilMeterReadingMonth = payment.details?.monthsUntilMeterReadingMonth;
  const meterType = payment.meterDetails?.meterType;

  if (monthsUntilMeterReadingMonth == null) return false;
  return (
    (meterType === 'Analogue' && monthsUntilMeterReadingMonth === 0) || // analog meter = current month
    (meterType === 'Digital' && monthsUntilMeterReadingMonth === 1) // digital meter = next month
  );
};

/**
 * A hook to fetch the advances for a specific address and provide shared logic
 * @param addressIdentifierParam
 * @param notificationFields - Fields object for notification content (different structure for different components)
 * @param swrConfig - Pass `{ suspense: true }` when the tree is wrapped in `<Suspense>`
 */
export const useAdvances = (
  addressIdentifierParam?: string,
  notificationFields?: NotificationBoxProps,
  swrConfig?: { suspense?: boolean },
) => {
  const suspense = swrConfig?.suspense ?? false;
  const {
    selectedAccount: { crmAccountNumber: accountNumber },
    isCurrentAccountReader,
  } = useSelfServiceAccount();
  const { query } = useRouter();
  const { data: session } = useSession();
  const { mutate } = useSWRConfig();
  const setNotification = useSetNotificationAndScrollToTop();
  const { trackEventBE } = useTracking();

  const addressIdentifier =
    addressIdentifierParam ??
    resolveDeliveryAddressFromQuery({
      accountNumber,
      queryAddress: query[ADDRESS_PARAM],
    }) ??
    '';
  const advanceCacheKey = `/accounts/${accountNumber}/delivery-addresses/${addressIdentifier}/advance-amounts`;

  const { data, isLoading, error } = useAuthenticatedDCBE(
    getEnecoBeXapiSiteApiV1AccountsByAccountNumberDeliveryAddressesByAddressIdentifierAdvanceAmounts,
    {
      path: {
        accountNumber,
        addressIdentifier,
      },
    },
    [advanceCacheKey],
    session,
    { shouldFetch: !!addressIdentifier, suspense },
  );

  const allAdvancesIncludingInactives = unwrapData(data);
  const path = { accountNumber, addressIdentifier };

  const activeAdvances = useMemo(
    () =>
      filteredAdvancesWithActiveContractAndCurrentAmount({
        advances: allAdvancesIncludingInactives,
      }),
    [allAdvancesIncludingInactives],
  );

  const currentAmount = activeAdvances?.totals?.currentAmount;

  const isInMeterReadingMonth = activeAdvances?.advancePayments?.some(isPaymentInMeterReadingMonth);

  const isDefaulter = allAdvancesIncludingInactives?.advancePayments?.some(x => x.details?.isDefaulter);

  // budget meters have no currentAmount, so they never survive the activeAdvances filter
  const advancesWithActiveContract =
    allAdvancesIncludingInactives?.advancePayments?.filter(advance => hasActiveContract({ advance })) ?? [];

  const hasOnlyBudgetMeters =
    advancesWithActiveContract.length > 0 &&
    advancesWithActiveContract.every(advance => advance.meterDetails?.isBudgetMeter === true);

  const reconcileAdvanceAmount = calculateReconcileGlobalWithExistingCurrentAmounts({
    advances: activeAdvances,
  });

  const hideEdit = !canEditAdvanceAmount({
    isCurrentAccountReader,
    advancePayments: activeAdvances?.advancePayments ?? [],
  });

  const submitGlobalRecommendedAdvance = async ({ amount }: { amount: number | null | undefined }) => {
    if (amount == null) return;

    try {
      const { response } =
        await postEnecoBeXapiSiteApiV1AccountsByAccountNumberDeliveryAddressesByAddressIdentifierAdvanceAmounts({
          path: { ...path },
          body: { amount, type: 'Global' },
        });
      if (!response.ok) {
        throw new Error('request failed');
      }
      trackEventBE('be_bbp_advice', { status: 'success' });
    } catch (e) {
      void e;
      trackEventBE('be_bbp_advice', { status: 'failed' });
      throw new Error('request failed');
    }

    await mutate(advanceCacheKey);

    if (notificationFields) {
      setNotification({
        notificationOptions: {
          title: notificationFields.title,
          text: <RichText html={notificationFields.text?.toString()} />,
          variant: notificationFields.variant,
        },
        shouldScroll: true,
      });
    }
  };

  return {
    advances: allAdvancesIncludingInactives,
    addressIdentifier,
    path,
    session,
    advanceCacheKey,
    isLoading,
    error,
    activeAdvances,
    currentAmount,
    isInMeterReadingMonth,
    isDefaulter,
    hasOnlyBudgetMeters,
    reconcileAdvanceAmount,
    hideEdit,
    submitGlobalRecommendedAdvance,
    isCurrentAccountReader,
  };
};
